import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../hooks';
import { usersApi } from '../services/api';
import CreditLimitsForm from '../components/organizations/CreditLimitsForm';
import ScrollHeader from '../components/common/ScrollHeader';
import SettingsSkeleton from '../components/common/SettingsSkeleton';
import { CreditLimits } from '../types';

const Settings: React.FC = () => {
  const { user: currentUser } = useAppSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Password change
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  // Profile settings
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
  });

  // Credit limits
  const [creditLimits, setCreditLimits] = useState<CreditLimits>({
    credit_limit: 100.0,
    access_type: ['web'],
  });

  useEffect(() => {
    const initializeData = () => {
      setProfileData({
        first_name: currentUser?.first_name || '',
        last_name: currentUser?.last_name || '',
        email: currentUser?.email || '',
      });
      setCreditLimits({
        credit_limit: currentUser?.credit_limit || 100.0,
        access_type: currentUser?.access_type || ['web'],
      });
      setLoading(false);
    };

    initializeData();
  }, [currentUser]);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    try {
      setSaving(true);
      await usersApi.updateUser(currentUser!.user_id, {
        password: passwordData.new_password,
        current_password: passwordData.current_password,
      });
      setMessage({ type: 'success', text: 'Password updated successfully' });
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update password' });
    } finally {
      setSaving(false);
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      await usersApi.updateUser(currentUser!.user_id, profileData);
      setMessage({ type: 'success', text: 'Profile updated successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update profile' });
    } finally {
      setSaving(false);
    }
  };

  const handleCreditLimitsUpdate = async (limits: CreditLimits) => {
    try {
      setSaving(true);
      await usersApi.updateUserCreditLimits(currentUser!.user_id, limits);
      setCreditLimits(limits);
      setMessage({ type: 'success', text: 'Credit limits updated successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update credit limits' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div>
          <ScrollHeader title="Settings" />
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Manage your account settings and preferences
          </p>
        </div>
        <SettingsSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <ScrollHeader title="Settings" />
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Manage your account settings and preferences
        </p>
      </div>

      {message && (
        <div className={`p-4 rounded-xl ${
          message.type === 'success' 
            ? 'bg-primary-500/10 border border-primary-500/20 text-primary-500' 
            : 'bg-red-500/10 border border-red-500/20 text-red-500'
        }`}>
          {message.text}
        </div>
      )}

      {/* Profile Settings */}
      <div className="rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border p-6">
        <h3 className="text-xl text-gray-900 dark:text-white mb-6">Profile Settings</h3>
        <form onSubmit={handleProfileUpdate} className="space-y-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="first_name" className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                First Name
              </label>
              <input
                type="text"
                name="first_name"
                id="first_name"
                value={profileData.first_name}
                onChange={(e) => setProfileData(prev => ({ ...prev, first_name: e.target.value }))}
                disabled={saving}
                className="w-full bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
            <div>
              <label htmlFor="last_name" className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                Last Name
              </label>
              <input
                type="text"
                name="last_name"
                id="last_name"
                value={profileData.last_name}
                onChange={(e) => setProfileData(prev => ({ ...prev, last_name: e.target.value }))}
                disabled={saving}
                className="w-full bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>
          <div>
            <label htmlFor="email" className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
              Email
            </label>
            <input
              type="email"
              name="email"
              id="email"
              value={profileData.email}
              onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
              disabled={saving}
              className="w-full bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Updating...' : 'Update Profile'}
            </button>
          </div>
        </form>
      </div>

      {/* Password Change */}
      <div className="rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border p-6">
        <h3 className="text-xl text-gray-900 dark:text-white mb-6">Change Password</h3>
        <form onSubmit={handlePasswordChange} className="space-y-6">
          <div>
            <label htmlFor="current_password" className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
              Current Password
            </label>
            <input
              type="password"
              name="current_password"
              id="current_password"
              value={passwordData.current_password}
              onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
              required
              disabled={saving}
              className="w-full bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
          <div>
            <label htmlFor="new_password" className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
              New Password
            </label>
            <input
              type="password"
              name="new_password"
              id="new_password"
              value={passwordData.new_password}
              onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
              required
              disabled={saving}
              className="w-full bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
          <div>
            <label htmlFor="confirm_password" className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
              Confirm New Password
            </label>
            <input
              type="password"
              name="confirm_password"
              id="confirm_password"
              value={passwordData.confirm_password}
              onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
              required
              disabled={saving}
              className="w-full bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Updating...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>

      {/* Credit Limits */}
      {(currentUser?.role === 'super_admin' || currentUser?.role === 'org_admin') && (
        <div className="rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border p-6">
          <h3 className="text-xl text-gray-900 dark:text-white mb-6">Credit Limits & Access</h3>
          <CreditLimitsForm
            limits={creditLimits}
            onChange={handleCreditLimitsUpdate}
            isOrgAdmin={currentUser?.role === 'org_admin'}
            disabled={saving}
            currentUserRole={currentUser?.role}
          />
        </div>
      )}
    </div>
  );
};

export default Settings;
